import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquareText, Target, RefreshCw, UserCheck, Scale, UsersRound,
  ShieldAlert, ClipboardList, TrendingUp, GraduationCap, ListChecks, MessageCircleQuestion, Clock, ListTodo,
} from 'lucide-react';
import api from '../../../services/api';
import { INBOX_COUNT_EVENT } from '../../../hooks/performanceInboxBadge';

// P2 Faz 1 / Paket C — GET v1/performance/inbox ([Authorize]-only; kullanıcının
// KENDİ bekleyen performans işleri). Yanıt gövdesi List<InboxItemDto>:
//   { kind, title, subtitle, dueAt, overdue, link, subjectId }
// `link` kayda özgüdür ve OTORİTERDİR (2026-10-28 — backend PerformanceInboxLinks):
// kart işin ekranını O KAYIT için açar; `subjectId` kaydın kimliğidir.
// PM aboneliği olmayan tenant 409 "module.notSubscribed" döner — sayfa rota
// düzeyinde zaten moduleGated olduğundan bu yol pratikte görünmez; yine de
// skipErrorToast ile sessiz boş duruma düşülür (useMyPip/useMyCalibration
// deseni: pasif "gelen kutum" okuması için sahte hata toast'ı üretilmez).
export const usePerformanceInbox = () => {
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    api.get('/v1/performance/inbox', { skipErrorToast: true })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.status ? (res.data.data || []) : [];
        setItems(list);
        // Sol menü rozeti 60 sn önbellekli sayaç ucundan beslenir; sayfa
        // buradayken kesin sayı BUDUR (bkz. usePerformanceInboxBadge).
        window.dispatchEvent(new CustomEvent(INBOX_COUNT_EVENT, { detail: { count: list.length } }));
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  return { items, fetching, refresh };
};

// BE'nin ürettiği türler (InboxItemDto.Kind). Etiketleri `performance.inbox.kind.<kind>`
// anahtarından gelir; ikon + vurgu rengi burada eşlenir. Bilinmeyen bir tür
// gelirse FallbackIcon kullanılır ve etiket ham kind'e düşer (defaultValue) —
// sessiz boş kart YOK.
//
// HEDEF BAĞLANTI BURADA EŞLENMEZ: müşteri geri bildirimiyle (2026-10-28) BE her
// satır için işin yapıldığı ekranı o kayıtla açan bağlantıyı kendisi üretir
// (ör. feedback_fill → /performance/fill-feedback?responseId=…, feedback_request
// → /feedback/send?requestId=… — talebi yanıtlayan, alıcısı kilitli gönderim
// ekranı; pm_action → /performance/my-actions — sorumlunun kendi listesi). Eski
// `link` override'ları (feedback_request → Taleplerim sekmesi, pm_action →
// my-actions) bu yüzden kaldırıldı; tek doğru kaynak BE'dir.
//
// `goal_stale`: Skorlama Faz F, Task 5 (spec §4, task-3-report.md §1.3) —
// BE'nin gönderdiği `subtitle` ÇIPLAK bir gün sayısı (ör. "23"), bir cümle
// DEĞİL (i18n YOK, "hedef adı + gün sayısı token'ı" — task-3-brief.md birebir).
// Bu fonksiyon VARSA PerformanceInboxPage onu ham `item.subtitle` yerine
// kullanır.
const staleGoalsSubtitle = (item, t) => t('staleGoals.badge', { days: item.subtitle });

export const KIND_META = {
  feedback_fill: { Icon: MessageSquareText, tone: 'info' },
  feedback_request: { Icon: MessageCircleQuestion, tone: 'info' },
  goal_decision: { Icon: Target, tone: 'primary' },
  goal_revision: { Icon: RefreshCw, tone: 'primary' },
  nomination_approval: { Icon: UserCheck, tone: 'info' },
  calibration_employee: { Icon: Scale, tone: 'success' },
  calibration_manager: { Icon: UsersRound, tone: 'success' },
  objection_review: { Icon: ShieldAlert, tone: 'danger' },
  performance_task: { Icon: ClipboardList, tone: 'warning' },
  pip_action: { Icon: TrendingUp, tone: 'warning' },
  idp_approval: { Icon: GraduationCap, tone: 'purple' },
  goal_stale: { Icon: Clock, tone: 'warning', subtitle: staleGoalsSubtitle },
  // Analitik Plan 4 — sorumlusu olunan açık performans aksiyonu (son tarih aksiyondan). Hedefi BE üretir: sorumlunun kendi
  // listesi (/performance/my-actions); /performance/actions İK yönetim listesidir.
  pm_action: { Icon: ListTodo, tone: 'primary' },
};

export const FallbackIcon = ListChecks;

export default usePerformanceInbox;
